import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useMetaData } from '../../../../contexts/metadata';

type Mode = 'W' | 'D';

interface EpxOncologyData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  mode?: Mode;
  shouldAskMeds: boolean;
  shouldAskOtherSymptoms: boolean;
  hasQOL: boolean;
  hasHRSN: boolean;
  times: string[];
  days: number[];
  step?: number;
}

type EpxOncologyDataRaw = DateSerialized<EpxOncologyData, 'start' | 'end'>;

export const EpxOncologyDefaultData: EpxOncologyData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  mode: 'W',
  shouldAskMeds: false,
  shouldAskOtherSymptoms: false,
  hasQOL: true,
  hasHRSN: false,
  times: ['09:00'],
  days: [],
  step: 1
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxOncologyFormProps {
  setForm?: (form: FormInstance<EpxOncologyDataRaw>) => void;
  onSubmit?: (data: EpxOncologyData) => void;
  isDisabled?: boolean;
}

const EpxOncologyForm = ({
  setForm,
  onSubmit: _onSubmit,
  isDisabled
}: EpxOncologyFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const serialize = (data: EpxOncologyDataRaw): EpxOncologyData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT)
    };
  };
  const deserialize = (data: EpxOncologyData): EpxOncologyDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxOncologyDefaultData);
  const form = new FormInstance<EpxOncologyDataRaw, EpxOncologyData>(
    defaultData,
    { serialize, deserialize }
  );

  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxOncologyData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {() => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="times[0]" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Weekday name="days" minItems={1} abbreviated inline />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask about Quality of Life (PHQ2)"
                      name="hasQOL"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked
                      questions about their mood and quality of life.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask about HRSN"
                      name="hasHRSN"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked about
                      health-related social needs. The four possible needs a{' '}
                      {terms.patient.default} can report are housing, transport
                      to appointments or the grocery store, food preparation,
                      and access to a caregiver.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask about Medication Tracking "
                      name="shouldAskMeds"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked about
                      their adherence to prescribed medications.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask about Other Symptoms"
                      name="shouldAskOtherSymptoms"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked if they
                      wish to report other symptoms to a care manager.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxOncologyForm;
